/**
 * BassStrategies.js
 * Specialized procedural generation for Bass lines.
 * Defines the "Groove Foundation" for specific sub-genres.
 */

import { BASS_CONFIG } from '../data/StrategyConfig.js';

// --- UTILITIES ---

/**
 * Helper: Apply variance to a value.
 */
const fuzz = (value, variance, random) => {
    if (!random) return value;
    const range = variance * 2;
    return value + ((random.next() * range) - variance);
};

/**
 * Helper: Create a note event
 */
const createNote = (s, d, len, vel, artic = null, slide = false) => ({
    s, d, len, vel, notes: [d], articulation: artic, slide
});

/**
 * Helper: Apply Micro-Timing and Swing
 * Tagged [TYPE: GROOVE]
 */
const applyMicroTiming = (events, context) => {
    const { traits } = context;
    if (!traits) return events;

    const groove = traits.groove || 'straight';
    const swingAmount = traits.swing || 0; // 0.0 - 1.0

    // Timing Offsets (in beats)
    let offset = 0;
    if (groove === 'laid_back') offset = 0.02;
    if (groove === 'rushed') offset = -0.01;
    if (groove === 'drunk') offset = (Math.random() - 0.5) * 0.05;

    return events.map(ev => {
        let newStart = ev.s;

        // Apply Swing (Standard 8th note swing)
        // Delay the "and" of the beat (x.5)
        if (swingAmount > 0) {
            const beatPos = newStart % 1; // 0.0 to 0.999
            // Check if it's an offbeat 8th (approx 0.5)
            // We allow some tolerance (0.45 - 0.55)
            if (beatPos >= 0.45 && beatPos <= 0.55) {
                // Max swing (triplet) moves 0.5 -> 0.666 (approx +0.16)
                const swingShift = 0.16 * swingAmount;
                newStart += swingShift;
            }
        }

        return {
            ...ev,
            s: newStart + offset
        };
    });
};

// --- STRATEGIES ---

/**
 * STRATEGY: ROOT-FIFTH (March, Country, Polka)
 * Alternates Root and Fifth on downbeats.
 * 1 . 2 . 3 . 4 .
 * R . 5 . R . 5 .
 */
export const generateRootFifthBass = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default; // C2 range
    const fifth = root + 7;
    const random = context.random;
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    for (let beat = 0; beat < beats; beat++) {
        // Beat 1 & 3: Root, Beat 2 & 4: Fifth
        const pitch = (beat % 2 === 0) ? root : fifth;
        const vel = fuzz(BASS_CONFIG.velocities.strong, 0.05, random);

        events.push(createNote(
            barStart + beat,
            pitch,
            BASS_CONFIG.durations.quarter,
            Math.min(1.0, vel),
            'marcato'
        ));
    }
    return applyMicroTiming(events, context);
};

/**
 * STRATEGY: OCTAVES (Driving Rock, Epic)
 * Plays Root and Octave simultaneously or alternating rapidly.
 * This implementation alternates 8th notes for drive: Low - High - Low - High
 */
export const generateOctaveBass = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default; // C2
    const octave = root + 12; // C3
    const random = context.random;
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));
    const eighths = beats * 2;

    for (let i = 0; i < eighths; i++) { // 8th notes
        const time = Math.round((barStart + (i * 0.5)) * 1000) / 1000;
        const pitch = (i % 2 === 0) ? root : octave;
        const vel = fuzz(0.85, 0.05, random);

        events.push(createNote(
            time,
            pitch,
            fuzz(0.25, 0.02, random), // Short
            Math.min(1.0, vel),
            'short'
        ));
    }
    return events;
};

/**
 * STRATEGY: OCTAVE PUMP (Disco, House)
 * Root on downbeat, Octave on offbeat.
 * 1 . & . 2 . & .
 * R . 8 . R . 8 .
 */
export const generateOctavePump = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default; // C2 range
    const random = context.random; // Passed from MusicEngine
    const traits = context.traits || {};
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    // KICK LOCK LOGIC (Updated Phase 1)
    // Use context.kickPatternEvents (absolute times)
    // If enabled, we strictly sync with Kick events in this bar.
    if (traits.kickLock && context.kickPatternEvents) {
        // Filter kick events relevant to this bar
        const barKicks = context.kickPatternEvents.filter(k => k.s >= barStart && k.s < barStart + beats);

        if (barKicks.length > 0) {
            barKicks.forEach(k => {
                const beatPos = k.s - barStart; // relative
                // Use Root for downbeats (approx x.0), Octave for offbeats
                const isDownbeat = Math.abs(beatPos % 1) < 0.1;
                const pitch = isDownbeat ? root : root + 12;

                events.push(createNote(k.s, pitch, 0.2, 0.9, 'short'));
            });
            return applyMicroTiming(events, context);
        }
        // If no kicks found in this bar, fallback to standard pump
    }

    // Standard Pump
    for (let beat = 0; beat < beats; beat++) {
        // Downbeat: Root (Short, punchy)
        events.push(createNote(
            barStart + beat,
            root,
            fuzz(0.2, 0.02, random), // Short duration for punch
            fuzz(0.9, 0.05, random),
            'short'
        ));

        // Offbeat: Octave (Higher velocity for the "pump")
        // Variation: Occasional double pump or silence?
        // 10% chance to drop the octave for a "breather"
        if ((random ? random.next() : Math.random()) > 0.1) {
            events.push(createNote(
                barStart + beat + 0.5,
                root + 12,
                fuzz(0.2, 0.02, random),
                fuzz(0.7, 0.05, random), // Slightly ghosted or full? Disco usually full.
                'short'
            ));
        }
    }
    return applyMicroTiming(events, context);
};

/**
 * STRATEGY: 808 GLIDE (Trap, Drill)
 * Long sustained roots with occasional pitch slides/bends at end of bar.
 * Round 3 - Phase 4: Envelope Aware (Monophony Enforcement)
 */
export const generate808Glide = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.low; // C1 (Very Low 808)
    const isTransition = context.isTransition; // Need to pass this context
    const random = context.random;
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    // Envelope Awareness: 808s must not overlap heavily (Phase cancellation).
    // Ensure duration is long but clean.

    const duration = isTransition ? (beats / 2.0) : fuzz(beats - 0.2, 0.1, random);

    // Main 808 Hit on Beat 1
    events.push(createNote(
        barStart,
        root,
        duration,
        1.0,
        'long'
    ));

    // Optional Secondary Hit (Beat 3 or 3.5 if 4/4)
    // Randomized placement based on beats
    if (beats >= 4 && (random ? random.next() : Math.random()) > 0.4) {
        const offset = (random ? random.next() : Math.random()) > 0.5 ? 2.5 : 2.0; // on '3' or '3&'
        events.push(createNote(
            barStart + offset,
            root,
            1.0,
            fuzz(0.9, 0.05, random),
            'long'
        ));
    }

    // Glide Logic: Transitions or random interest
    if ((random ? random.next() : Math.random()) > 0.7) {
        const interval = (random ? random.next() : Math.random()) > 0.5 ? 12 : 7;
        const glideStart = barStart + beats - 0.25;
        events.push(createNote(
            glideStart, // Last 16th
            root + interval, // Octave or 5th up
            0.25,
            0.8,
            'slide',
            true // Flag for Glide
        ));
    }

    return events;
};

/**
 * STRATEGY: LOFI BASS (Lofi / Chill)
 * Simple, deep roots. Occasional melodic fill on the "and" of 4.
 * Uses 808 sub range or low upright.
 * Round 3 - Phase 4: Envelope Aware
 */
export const generateLofiBass = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.low; // Deep (C1)
    const random = context.random;
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    // Detect Instrument Type from Context (if available)
    // Note: 'instrument' must be injected into context by MusicEngine/Pipeline
    const instrumentId = (context.instrument && context.instrument.id) ? context.instrument.id.toLowerCase() : '';
    const isSub = instrumentId.includes('sub') || instrumentId.includes('808');
    const isUpright = instrumentId.includes('upright') || instrumentId.includes('acoustic');

    // Envelope Logic
    let baseVel = 0.8;
    let baseDur = beats / 2.0;

    if (isSub) {
        baseVel = 0.9; // Subs need drive
        baseDur = beats - 0.5; // Sustain to fill space
    } else if (isUpright) {
        baseVel = 0.7; // Thumpy, dynamic
        baseDur = 1.0; // Decay is faster
    }

    // Sustained Root on 1 (and maybe 3)
    events.push(createNote(
        barStart,
        root,
        fuzz(baseDur, 0.2, random),
        fuzz(baseVel, 0.1, random),
        'long'
    ));

    // Optional: Hit on beat 3 or 2-and (if 4/4)
    if (beats >= 4 && (!isSub || (random ? random.next() : Math.random()) > 0.8)) {
        if ((random ? random.next() : Math.random()) > 0.6) {
            events.push(createNote(
                barStart + 2.5, // 3-and (syncopated)
                root,
                1.0,
                fuzz(baseVel * 0.9, 0.1, random),
                'long'
            ));
        }
    }

    // Melodic Fill (High octave)
    if ((random ? random.next() : Math.random()) > 0.7) {
        const fillNote = root + 12 + (chord.quality === 'minor' ? 3 : 4); // 10th
        events.push(createNote(
            barStart + beats - 0.5, // Last 8th
            fillNote,
            0.5,
            0.6, // Quiet fill
            'legato'
        ));
    }

    return applyMicroTiming(events, context);
};

/**
 * STRATEGY: MOTIF RIFF (Melodic Bass)
 * Plays the actual melodic HEAD of the motif in the bass register.
 * Used for "Unison Riffs" (Rock/Metal/Epic)
 */
export const generateMotifRiffBass = (chord, barStart, context) => {
    const events = [];
    const motifManager = context.motifManager;

    if (!motifManager) return generateRunningEighths(chord, barStart, context); // Fallback

    // Get the HEAD fragment of the motif
    const sourceMotif = motifManager.getMotifForSection(chord.section || 'VERSE');
    const head = motifManager.getFragment('head', sourceMotif);

    if (!head || head.length === 0) return generateRunningEighths(chord, barStart, context);

    // Transpose to Bass Range (C2)
    // Motif is usually relative to key root (C4).
    // We need to shift it down 2 octaves (-24).
    // AND adapt to current chord?
    // Unison riffs usually ignore harmony and play the line against a pedal point,
    // OR the line *defines* the harmony.

    // For now, simple transposition + Chord Lock
    // We rely on MotifManager's adaptMotifToHarmony if we want tonal adherence.
    // But riffs are often scalar.

    // 1. Adapt to current Harmony
    const adaptedHead = motifManager.adaptMotifToHarmony(head, chord);

    // 2. Shift to Bass
    adaptedHead.forEach(note => {
        // Shift down 2 octaves
        const pitch = note.d - 24;
        // Ensure range
        const finalPitch = Math.max(24, Math.min(60, pitch));

        events.push(createNote(
            barStart + note.s,
            finalPitch,
            note.len,
            fuzz(BASS_CONFIG.velocities.accent, 0.05, motifManager.random),
            'riff' // heavy articulation
        ));
    });

    return applyMicroTiming(events, context);
};

/**
 * Helper: Generate a deterministic Funk/Groove pattern (2 bars / 32 steps)
 */
const generateFunkPattern = (syncopationLevel = 50, random) => {
    // 32 steps (2 bars of 16th notes)
    const pattern = new Array(32).fill(null);

    // Guaranteed hits: The One of Bar 1 (and maybe Bar 2)
    pattern[0] = { type: 'root', artic: 'slap', vel: 1.0 };
    // Beat 1 of Bar 2 (Step 16) - usually strong, but can be dropped for heavy syncopation
    if ((random ? random.next() : Math.random()) > 0.3) {
        pattern[16] = { type: 'root', artic: 'slap', vel: 0.9 };
    }

    // Iterate through remaining steps
    for (let i = 1; i < 32; i++) {
        if (pattern[i]) continue; // Skip already set

        const barPos = i % 16;
        const beatPos = barPos % 4; // 0=Down, 1=e, 2=&, 3=a
        const isDownbeat = beatPos === 0;
        const isOffbeat = beatPos === 2; // The "&"
        const isSixteenth = beatPos === 1 || beatPos === 3; // "e" and "a"

        let probability = 0.0;

        if (isDownbeat) {
            // On beats 2, 3, 4
            // High syncopation reduces on-beat hits to emphasize spaces
            probability = 0.7 - (syncopationLevel * 0.005);
        } else if (isOffbeat) {
            // The "&"
            probability = 0.4 + (syncopationLevel * 0.002);
        } else {
            // The 16ths
            probability = 0.1 + (syncopationLevel * 0.005);
        }

        if ((random ? random.next() : Math.random()) < probability) {
            // Determine Note Type
            const isPop = !isDownbeat; // Off-beats/16ths usually pops in funk

            // 30% chance of being a ghost note instead of a full hit
            const isGhost = (random ? random.next() : Math.random()) < 0.3;

            let type = 'root';
            let artic = 'slap';
            let vel = 0.8;

            if (isGhost) {
                 artic = 'ghost';
                 vel = 0.4;
            } else if (isPop) {
                 type = (random ? random.next() : Math.random()) > 0.6 ? 'octave' : 'seventh';
                 artic = 'pop';
                 vel = 0.9;
            }

            pattern[i] = { type, artic, vel };
        }
    }
    return pattern;
};

/**
 * STRATEGY: FUNK SLAP (Funk, Fusion)
 * Syncopated 16th notes. Root on 1. Octaves/7ths on "e" and "a".
 * Heavily syncopated.
 */
export const generateFunkSlap = (chord, barStart, context) => {
    // Wiring Up Ghost Config: syncopationChance
    let syncopationLevel = context.syncopationLevel !== undefined ? context.syncopationLevel : BASS_CONFIG.funk.defaultSyncopation;

    // Override/Blend with seeded config if available
    if (context.config && context.config.rhythmProfile && context.config.rhythmProfile.syncopationChance !== undefined) {
        // Map 0.0-1.0 to 0-100. Blend 50/50 with trait level? Or Replace?
        // Replace is cleaner for "Seeded" control.
        syncopationLevel = context.config.rhythmProfile.syncopationChance * 100;
    }

    const random = context.random;

    // Retrieve or Generate Pattern
    let pattern = null;
    const sectionKey = chord.section || 'default';

    if (context.cache) {
        if (!context.cache[BASS_CONFIG.funk.cacheKey]) context.cache[BASS_CONFIG.funk.cacheKey] = {};
        if (!context.cache[BASS_CONFIG.funk.cacheKey][sectionKey]) {
            // Generate unique pattern for this section
            context.cache[BASS_CONFIG.funk.cacheKey][sectionKey] = generateFunkPattern(syncopationLevel, random);
        }
        pattern = context.cache[BASS_CONFIG.funk.cacheKey][sectionKey];
    } else {
        // Generates pattern on the fly if cache is missing (deterministic if seeded random is provided)
        pattern = generateFunkPattern(syncopationLevel, random);
    }

    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default; // C2
    const octave = root + 12;
    const seventh = root + 10;

    // Map Pattern to Current Bar
    // Calculate offset in the pattern (assuming 4/4)
    // We need to know which bar of the section we are in to map to a 2-bar pattern
    // barIndex is usually available in context or chord
    const barIndex = context.barIndex || (chord.barIndex || 0);
    const patternLen = 32; // 2 bars of 16ths
    const stepsPerBar = (chord.timeSig || 4) * 4; // 16ths
    const patternOffset = (Math.floor(barIndex) * 16) % patternLen; // Simple offset for now

    for (let i = 0; i < stepsPerBar; i++) {
        const patternStep = (patternOffset + i) % patternLen;
        const hit = pattern[patternStep];

        if (hit) {
            const time = barStart + (i * 0.25);
            let pitch = root;
            if (hit.type === 'octave') pitch = octave;
            if (hit.type === 'seventh') pitch = seventh;

            events.push(createNote(time, pitch, 0.15, hit.vel, hit.artic));
        }
    }

    return events;
};

/**
 * STRATEGY: GALLOP (Metal, Hard Rock)
 * 16th-16th-8th pattern. Constant driving root.
 */
export const generateGallop = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default; // C2
    const random = context.random;
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    // Metal variation: Invert gallop? (8th - 16th - 16th)
    const isInverted = (random ? random.next() : Math.random()) > 0.7;

    for (let beat = 0; beat < beats; beat++) {
        if (isInverted) {
             // 8th - 16th - 16th
             events.push(createNote(barStart + beat, root, 0.3, fuzz(1.0, 0.02, random), 'heavy'));
             events.push(createNote(barStart + beat + 0.5, root, 0.15, fuzz(0.8, 0.05, random), 'palm_mute'));
             events.push(createNote(barStart + beat + 0.75, root, 0.15, fuzz(0.8, 0.05, random), 'palm_mute'));
        } else {
             // 16th - 16th - 8th (Standard Gallop)
             events.push(createNote(barStart + beat, root, 0.15, fuzz(0.8, 0.05, random), 'palm_mute'));
             events.push(createNote(barStart + beat + 0.25, root, 0.15, fuzz(0.8, 0.05, random), 'palm_mute'));
             events.push(createNote(barStart + beat + 0.5, root, 0.3, fuzz(1.0, 0.02, random), 'heavy'));
        }
    }

    return events;
};

/**
 * STRATEGY: STEPWISE (Orchestral, Jazz, Hymn)
 * Uses inversions to create a smooth bass line.
 */
export const generateStepwiseBass = (chord, barStart, context) => {
    const events = [];
    let note = chord.rootPitchClass; // Default root
    const nextChord = context.nextChord;
    const random = context.random;

    // Check interval to next chord
    if (nextChord) {
        const nextRoot = nextChord.rootPitchClass;
        // Calculate semitone distance
        const dist = Math.abs((nextRoot % 12) - (note % 12));
        const distWrapped = Math.min(dist, 12 - dist);

        // Check 3rd of current chord
        const third = (note + (chord.quality === 'minor' ? 3 : 4)) % 12;
        const fifth = (note + 7) % 12;

        const distFrom3rd = Math.min(Math.abs((nextRoot % 12) - third), 12 - Math.abs((nextRoot % 12) - third));
        const distFrom5th = Math.min(Math.abs((nextRoot % 12) - fifth), 12 - Math.abs((nextRoot % 12) - fifth));
        const distFromRoot = distWrapped;

        // Probabilistically choose inversion if it improves leading
        if ((random ? random.next() : Math.random()) > 0.4) { // Increased prob of inversion
            if (distFrom3rd < distFromRoot && distFrom3rd <= 2) {
                note = third; // Play 3rd (First Inversion)
            } else if (distFrom5th < distFromRoot && distFrom5th <= 2) {
                note = fifth; // Play 5th (Second Inversion)
            }
        }
    }

    // Offset to C2 range
    note = (note % 12) + BASS_CONFIG.octaves.default;

    // Create Whole Note (or dotted half)
    events.push(createNote(
        barStart,
        note,
        chord.duration * 0.9, // Sustain almost full chord duration
        fuzz(0.85, 0.05, random),
        'legato'
    ));

    return events;
};

/**
 * STRATEGY: LINE CLICHE (Fantasy, Sad)
 * Chromatic descent within the bar or across bars.
 * Since we generate bar-by-bar, we implement a "Bar Internal" chromatic walkdown
 * OR depend on context from previous bar (if supplied).
 * Let's do Bar Internal: Root -> Root-1 -> Root-2 -> Root-3 (e.g., A -> G# -> G -> F#)
 */
export const generateLineCliche = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default; // C2
    const { length, interval } = BASS_CONFIG.cliche;
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    // 4 notes descending chromatically
    for(let i=0; i<beats; i++) {
        // Chromatic descent: Root - i semitones
        const pitch = root - (i * interval);
        events.push(createNote(
            barStart + i, // Beat 1, 2, 3, 4
            pitch,
            fuzz(0.9, 0.05, context.random),
            fuzz(0.8, 0.05, context.random),
            'legato'
        ));
    }
    return events;
};

/**
 * STRATEGY: DRONE / PEDAL (Pastoral, Folk, Medieval)
 * Sustained root or fifth for the entire bar.
 */
export const generateDroneBass = (chord, barStart, context) => {
    // Determine interval: Root (most common) or Fifth (bagpipe style)
    // If context.isMedieval, maybe add fifth?
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.low; // Low drone
    const duration = chord.duration || BASS_CONFIG.drone.duration;

    // Root
    events.push(createNote(
        barStart,
        root,
        duration,
        fuzz(BASS_CONFIG.drone.velocity, 0.05, context.random),
        'long'
    ));

    // Optional Fifth (Bagpipe/Hurdy-Gurdy effect)
    // 50% chance if traits include 'folk' or 'medieval'
    const traits = context.traits || {};
    const isFolk = traits.name && (traits.name.includes('Medieval') || traits.name.includes('Folk'));

    if (isFolk && (context.random ? context.random.next() : Math.random()) > 0.5) {
        events.push(createNote(
            barStart,
            root + 7,
            duration,
            fuzz(BASS_CONFIG.drone.velocity * 0.8, 0.05, context.random), // Slightly quieter
            'long'
        ));
    }

    return events;
};

/**
 * STRATEGY: RUNNING EIGHTHS (Neo-Classical / Cello Ostinato)
 * Constant 8th notes on the root, or alternating octaves (but smoother than driving rock).
 */
export const generateRunningEighths = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default;
    const octave = root + 12; // Octave Up
    const random = context.random;
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));
    const eighths = beats * 2;
    const tension = context.getTension ? context.getTension(context.barIndex || 0) : 0.5;

    // Pattern Logic: High Tension -> Arpeggiation / More Octaves
    // Low Tension -> Static Root or Split
    let patternType = 'split';

    if (tension > 0.7) {
        patternType = 'arpeggio'; // New High Intensity Mode
    } else if (tension > 0.4) {
        patternType = (random ? random.next() : Math.random()) > 0.5 ? 'split' : 'alternating';
    } else {
        patternType = 'static'; // Mostly roots
    }

    const chordNotes = (chord.notes || []).map(n => (n % 12) + BASS_CONFIG.octaves.default);

    for (let i = 0; i < eighths; i++) {
        let pitch = root;

        if (patternType === 'arpeggio' && chordNotes.length > 0) {
            // 1-5-8-10 pattern or similar
            const noteIdx = i % chordNotes.length;
            pitch = chordNotes[noteIdx];
            // Add octave occasionally
            if (i % 2 === 1) pitch += 12;
        }
        else if (patternType === 'split') {
            if (i >= 4) pitch = octave;
        }
        else if (patternType === 'alternating') {
            if (i % 2 !== 0) {
                const roll = random ? random.next() : Math.random();
                pitch = (roll > 0.3) ? octave : root;
            }
        }
        // 'static' stays on root mostly

        const vel = fuzz(0.75, 0.05, random) + (i%2===0 ? 0.1 : 0);
        events.push(createNote(
            barStart + (i * 0.5),
            pitch,
            0.4, // Almost legato
            Math.min(1.0, vel), // Slight downbeat accent
            'detache'
        ));
    }
    return events;
};

/**
 * STRATEGY: WALTZ PULSE (Waltz)
 * Root on Beat 1. Silence on 2, 3 (handled by Chords/Comping).
 */
export const generateWaltzPulse = (chord, barStart, context) => {
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.low;
    return [createNote(barStart, root, 0.8, fuzz(0.9, 0.05, context.random), 'marcato')];
};

/**
 * STRATEGY: SYNTH PULSE (Cyber-Orch)
 * Aggressive 8th notes, sawtooth style.
 */
export const generateSynthPulse = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.low; // Deep
    const random = context.random;
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));
    const eighths = beats * 2;

    for (let i = 0; i < eighths; i++) {
        events.push(createNote(
            barStart + (i * 0.5),
            root,
            0.25, // Staccato
            fuzz(0.9, 0.05, random), // Driving
            'short'
        ));
    }
    return events;
};

/**
 * STRATEGY: NOIR WALKING (Noir / Slow Jazz)
 * Slow, deliberate walking bass (Half notes usually, or Quarter notes at slow tempo).
 * Sparse, focuses on Root and 5th/b7.
 */
export const generateNoirWalking = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default;
    // Beat 1: Root
    events.push(createNote(barStart, root, 1.8, fuzz(BASS_CONFIG.noir.velocity, 0.05, context.random), 'legato'));

    // Beat 3: Fifth or approach
    // If minor chord, maybe b7?
    const target = (context.nextChord ? context.nextChord.rootPitchClass : root) + BASS_CONFIG.octaves.default;
    const interval = target - root;
    let note2 = root + 7; // Default 5th

    // Approach logic
    if (Math.abs(interval) <= 2 && interval !== 0) {
        // Stepwise approach
        note2 = (interval > 0) ? root + 1 : root - 1;
    } else if (interval === 0) {
        // Stay on 5th
        note2 = root + 7;
    } else {
        // Chromatic approach from below
        note2 = target - 1;
    }

    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));
    if (beats >= 3) {
        events.push(createNote(barStart + 2, note2, 1.8, fuzz(BASS_CONFIG.noir.velocity, 0.05, context.random), 'legato'));
    }
    return events;
};

/**
 * STRATEGY: HOLIDAY PUMP (Christmas)
 * Root - 5 - 6 - 5 pattern (classic sleigh ride bass).
 */
export const generateHolidayPump = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default;

    // Quarter notes: R - 5 - 6 - 5
    const pattern = [0, 7, 9, 7]; // Intervals
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    for (let i = 0; i < beats; i++) {
        const interval = pattern[i % pattern.length];
        events.push(createNote(
            barStart + i,
            root + interval,
            0.5,
            fuzz(0.8, 0.05, context.random),
            'marcato'
        ));
    }
    return events;
};

/**
 * STRATEGY: TRAIN BEAT BASS (Western)
 * Root - 5th alternating in cut time (2-beat feel).
 */
export const generateTrainBeat = (chord, barStart, context) => {
    const events = [];
    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default;
    const fifth = root - 5; // 5th below usually for country/western alternating
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    for (let i = 0; i < beats; i++) {
        const pitch = (i % 2 === 0) ? root : fifth;
        events.push(createNote(barStart + i, pitch, BASS_CONFIG.western.duration, fuzz(BASS_CONFIG.western.velocity, 0.05, context.random), 'short'));
    }

    return events;
};

/**
 * STRATEGY: BAROQUE WALKING BASS (Basso Continuo)
 * Steady stream of 8th notes (0.5 duration).
 * Movement prioritizes scalar steps to the next root.
 */
export const generateBaroqueWalkingBass = (chord, barStart, context) => {
    if (!chord.notes) return [];
    const events = [];
    let currentPitch = chord.rootPitchClass + BASS_CONFIG.octaves.default; // Start on root (C2)
    const nextChordRoot = (context.nextChord ? context.nextChord.rootPitchClass : chord.rootPitchClass) + BASS_CONFIG.octaves.default;
    const random = context.random;

    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));
    const loopCount = beats * 2; // 8th notes

    for(let i=0; i<loopCount; i++) {
        // Downbeat (beat 1) usually Root
        // If i=0, strict root
        // If i=4 (beat 3), strong note (5th or 3rd or Root)

        let pitch = currentPitch;

        if (i === 0) {
            pitch = chord.rootPitchClass + BASS_CONFIG.octaves.default;
        } else {
            // Target the next strong beat or next chord
            // Simple logic: Move stepwise towards target?
            // Or random scalar walk?

            // Random +/- 1 or 2 scale steps
            // We need scale context? Pass chord notes as safe targets
            const notes = chord.notes.map(n => (n % 12) + BASS_CONFIG.octaves.default);
            // Add passing tones?
            // Let's just pick closest chord tone or neighbor

            // Look ahead to next chord if nearing end
            if (i >= (loopCount - 2) && context.nextChord) {
                // Target next root
                if (i === (loopCount - 1)) {
                    // Last 8th note: Approach from semitone below (Leading Tone) or above (Supertonic)
                    // Baroque often uses Dominant (5th) -> Tonic
                    // Let's use Closest Diatonic Step or Semitone
                    const diff = nextChordRoot - currentPitch;
                    if (Math.abs(diff) > BASS_CONFIG.baroque.stepThreshold) {
                        // Leap to neighbor of target
                         pitch = nextChordRoot - 1; // Chromatic approach from below
                    } else {
                         // Stepwise
                         pitch = (diff > 0) ? currentPitch + 1 : currentPitch - 1;
                    }
                } else {
                    // i === 6 (Beat 4)
                    // Move towards next root
                    const diff = nextChordRoot - currentPitch;
                    const step = (diff > 0) ? 1 : -1;
                    // Try to hit a chord tone
                    pitch = currentPitch + step;
                    // Snap to scale? (Simplified: Chromatic walk is fine for baroque bass)
                }
            } else {
                // Standard Walking (Beats 1, 2, 3)
                // Implement Scalar Run or Circle of Fifths logic

                // 1. Scalar Run Tendency
                // If we started a run, continue it?
                // Let's bias towards the direction of the Next Chord Root
                const direction = (nextChordRoot > currentPitch) ? 1 : -1;

                // Check if we can step in that direction and stay in chord/scale?
                // Simple Diatonic Step
                // Map currentPitch to Scale Degree... (Complex without full scale context)
                // Fallback: Chromatic/Whole tone step

                // 50% Chance: Arpeggiate (Leap to Chord Tone)
                // 50% Chance: Step towards target

                if ((random ? random.next() : Math.random()) > 0.5) {
                    // Arpeggiate: Pick random chord tone
                    pitch = notes[Math.floor((random ? random.next() : Math.random()) * notes.length)];
                } else {
                    // Stepwise Motion
                    // Try to move 2 semitones towards target (Whole step)
                    pitch = currentPitch + (direction * 2);
                }
            }
        }

        currentPitch = pitch;

        events.push(createNote(
            barStart + (i * 0.5),
            pitch,
            fuzz(0.4, 0.05, random), // Detached
            fuzz(0.8, 0.05, random),
            'short'
        ));
    }
    return events;
};

/**
 * STRATEGY: WALKING CHROMATIC (Jazz)
 * Aggressively uses chromatic approach notes on beats 2 and 4.
 */
export const generateWalkingChromatic = (chord, barStart, context) => {
    // Safety check: Restrict full chromatic walking to Jazz/Blues unless overridden
    const genre = (context.traits && context.traits.name) ? context.traits.name : '';
    const isJazz = genre.includes('Jazz') || genre.includes('Noir') || genre.includes('Blues') || genre.includes('Fusion');

    if (!isJazz && !context.forceChromatic) {
        return generateStepwiseBass(chord, barStart, context);
    }

    const events = [];
    let currentPitch = chord.rootPitchClass + BASS_CONFIG.octaves.default;
    const nextChordRoot = (context.nextChord ? context.nextChord.rootPitchClass : chord.rootPitchClass) + BASS_CONFIG.octaves.default;
    const random = context.random;
    const approachProfile = (context.traits && context.traits.approachNoteProfile) || 'chromatic_random';
    const beats = Math.ceil(chord.duration || (chord.timeSig || 4));

    for (let beat = 0; beat < beats; beat++) {
        let pitch = currentPitch;

        if (beat === 0) {
            // Strong beat 1: STRICTLY Root
            pitch = chord.rootPitchClass + BASS_CONFIG.octaves.default;
        } else if (beat === 2) {
            // Strong beat 3: STRICTLY Chord Tone
            if (chord.notes && chord.notes.length > 0) {
                const note = chord.notes[Math.floor((random ? random.next() : Math.random()) * chord.notes.length)];
                pitch = (note % 12) + BASS_CONFIG.octaves.default;
            } else {
                pitch = chord.rootPitchClass + 7 + BASS_CONFIG.octaves.default;
            }
        } else if (beat === beats - 1) { // Last beat approach
            // Chromatic Approach Target
            if (approachProfile === 'chromatic_below') {
                pitch = nextChordRoot - 1;
            } else if (approachProfile === 'chromatic_above') {
                pitch = nextChordRoot + 1;
            } else if (approachProfile === 'dominant_approach') {
                pitch = nextChordRoot + 7;
                if (pitch > currentPitch + 5) pitch -= 12;
            } else {
                const direction = (random ? random.next() : Math.random()) > 0.5 ? 1 : -1;
                pitch = nextChordRoot + direction;
            }
        } else {
            // Beat 2 or other: Passing tone
            pitch = currentPitch + ((random ? random.next() : Math.random()) > 0.5 ? 2 : -2);
        }

        currentPitch = pitch;
        events.push(createNote(barStart + beat, pitch, 0.8, fuzz(0.85, 0.05, random), 'legato'));
    }
    return applyMicroTiming(events, context);
};

/**
 * Custom Pattern Generator (Ostinato)
 * Reads `traits.bassPattern` (array of booleans or velocities) and maps root notes.
 */
export const generateCustomBassPattern = (chord, barStart, context) => {
    const events = [];
    const pattern = (context.traits && context.traits.bassPattern) ? context.traits.bassPattern : [];
    if (!Array.isArray(pattern) || pattern.length === 0) return events; // Fallback

    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default; // C2

    // Assume pattern is 16 steps (16th notes)
    pattern.forEach((stepVal, index) => {
        if (stepVal) {
            const time = barStart + (index * 0.25);
            const vel = (typeof stepVal === 'number') ? stepVal : 0.9;
            events.push(createNote(time, root, 0.2, fuzz(vel, 0.05, context.random), 'short'));
        }
    });

    return events;
};

/**
 * STRATEGY: MOTIF LOCKED (Cohesion)
 * Generates bass notes that strictly follow the rhythmic pattern of the global/section motif.
 * Uses Root and 5th.
 */
export const generateMotifLockedBass = (chord, barStart, context) => {
    const events = [];
    const motifManager = context.motifManager;

    if (!motifManager) return generateRootFifthBass(chord, barStart, context); // Fallback

    const rhythm = motifManager.getMotifRhythm(chord.section || 'VERSE');
    if (!rhythm || rhythm.length === 0) return generateRootFifthBass(chord, barStart, context);

    const root = chord.rootPitchClass + BASS_CONFIG.octaves.default; // C2
    const fifth = root + 7;

    rhythm.forEach((note, index) => {
        // Map notes: Strong beats = Root, Weak = 5th
        // Or simple alternation
        const pitch = (index % 2 === 0) ? root : fifth;

        events.push(createNote(
            barStart + note.s,
            pitch,
            note.l,
            fuzz(BASS_CONFIG.velocities.strong, 0.05, motifManager.random),
            'marcato'
        ));
    });

    return applyMicroTiming(events, context);
};

/**
 * STRATEGY: DYNAMIC BASS (Weighted Selector)
 * Swaps between Anchoring, Chromatic, Syncopated, and Motif-Locked behaviors based on probability.
 * NOW AWARE OF MELODY CONTOUR, GLOBAL TENSION, AND KICK CONTEXT.
 */
export const generateDynamicBass = (chord, barStart, context) => {
    const random = context.random;
    const tension = context.getTension ? context.getTension(chord.barIndex || 0) : 0.5;

    // 0. High Tension Overrides (Phase 6 Narrative)
    if (tension > 0.8) {
        // High Tension: Force Driving Bass (Running Eighths or Gallop)
        const roll = random ? random.next() : Math.random();
        if (roll > 0.5) return generateRunningEighths(chord, barStart, context);
        if (roll > 0.3) return generateGallop(chord, barStart, context);
        // Fallback to dynamic weighting
    }

    // 1. Check Contour Polarity (Phase 3)
    if (context.analysis && context.analysis.contourMap) {
        // Normalize barIndex to local chunk
        const barIndex = Math.floor(barStart / 4);
        // Support Global or Local Analysis
        let contour = context.analysis.contourMap[barIndex];
        if (contour === undefined && context.analysis.contourMap.length === 1) {
            contour = context.analysis.contourMap[0]; // Local chunk fallback
        }

        // If melody is ascending (1), force Bass Descending or Static
        // If melody is descending (-1), force Bass Ascending or Static

        if (contour === 1) {
            // Melody UP -> Bass DOWN (Line Cliche / Stepwise Descent)
            // Or just Root-Fifth (Static)
            if (random ? random.bool(0.4) : Math.random() < 0.4) {
                // Force Line Cliche (Descending)
                return generateLineCliche(chord, barStart, context);
            }
        } else if (contour === -1) {
            // Melody DOWN -> Bass UP (Walking Chromatic Up)
            if (random ? random.bool(0.4) : Math.random() < 0.4) {
                // Force Chromatic Approach (Ascending usually? No, it targets neighbor)
                // Let's use Stepwise which uses inversions
                // Or custom ascending line logic?
                // Stepwise usually finds shortest path.
                return generateStepwiseBass(chord, barStart, context);
            }
        }
    }

    // --- REFACTORED: Weights from Config ---
    const weightConfig = (context.config && context.config.bass) ? context.config.bass : {};
    let selectedWeights = weightConfig.default || {
        anchoring: 0.5,
        chromatic: 0.1,
        syncopated: 0.2,
        motif_locked: 0.2
    };

    // Adjust based on traits (Procedural Selection)
    if (context.traits) {
         if (context.traits.energy === 'high') {
             selectedWeights = weightConfig.high_energy || selectedWeights;
         } else if (context.traits.mood === 'complex') {
             selectedWeights = weightConfig.complex || selectedWeights;
         }
    }

    // Copy to mutable
    const weights = { ...selectedWeights };

    // Boost Motif Locking in Chorus/Climax
    if (chord.section && (chord.section.includes('CHORUS') || chord.section.includes('CLIMAX'))) {
        weights.motif_locked += 0.4;
    }

    // Weighted Random
    const total = weights.anchoring + weights.chromatic + weights.syncopated + weights.motif_locked;
    const r = (random ? random.next() : Math.random()) * total;

    let strategyFn = generateRootFifthBass;
    let accum = weights.anchoring;

    if (r < accum) {
        strategyFn = generateRootFifthBass;
    } else {
        accum += weights.chromatic;
        if (r < accum) strategyFn = generateWalkingChromatic;
        else {
            accum += weights.syncopated;
            if (r < accum) strategyFn = generateOctavePump;
            else strategyFn = generateMotifLockedBass;
        }
    }

    // Delegate
    const events = strategyFn(chord, barStart, context);

    // --- PHASE 1: KICK ATTRACTION (Context Aware Adjustment) ---
    // Post-process the events to lock onto kicks if close enough
    // Task 2: Groove Lock (Kick-Aware Bass) - Strengthened Logic
    if (context.kickPatternEvents && context.kickPatternEvents.length > 0) {
        const kickEvents = context.kickPatternEvents;
        const ATTRACTION_WINDOW = 0.1; // Increased to 0.1 (approx 16th triplet) to catch more

        // 1. Pocket Rule: Snap Bass to Kick if close
        let snappedEvents = events.map(ev => {
            // Check if there is a kick nearby
            const closeKick = kickEvents.find(k => Math.abs(k.s - ev.s) < ATTRACTION_WINDOW);
            if (closeKick) {
                return { ...ev, s: closeKick.s };
            }
            return ev;
        });

        // 2. The One: Ensure Bass on Beat 1 if Kick is on Beat 1
        const kickOnOne = kickEvents.some(k => Math.abs(k.s - barStart) < 0.01);
        const bassOnOne = snappedEvents.some(b => Math.abs(b.s - barStart) < 0.01);

        if (kickOnOne && !bassOnOne) {
            // Inject Root on One
            snappedEvents.unshift(createNote(
                barStart,
                chord.rootPitchClass + BASS_CONFIG.octaves.default,
                0.5,
                1.0,
                'accent'
            ));
        }

        // 3. INJECTION: If score depends on Lock, ensure Bass exists on every Kick
        // Logic: For every kick, if no bass note is at that time, add one.
        // Applies for 'driving' or 'action' genres usually.
        // Fix: Force injection for Epic Cinematic even at lower tensions to ensure Rhythmic Lock score
        const isEpic = (context.traits && context.traits.name && (context.traits.name.includes('Epic') || context.traits.name.includes('Cinematic')));
        const isDriving = (context.traits && context.traits.rhythm && (context.traits.rhythm.includes('driving') || context.traits.rhythm.includes('action'))) || tension > 0.6 || isEpic;

        if (isDriving) {
            kickEvents.forEach(k => {
                const hasBass = snappedEvents.some(b => Math.abs(b.s - k.s) < 0.01);
                if (!hasBass) {
                    snappedEvents.push(createNote(
                        k.s,
                        chord.rootPitchClass + BASS_CONFIG.octaves.default,
                        0.25,
                        0.9,
                        'short'
                    ));
                }
            });
            // Re-sort
            snappedEvents.sort((a,b) => a.s - b.s);
        }

        return snappedEvents;
    }

    return events;
};

// Dispatcher
export const getBassStrategy = (name, traits, context = {}) => {
    // Round 3 - Phase 4: Envelope Awareness
    // Check for custom pattern in traits first
    if (traits && traits.bassPattern && Array.isArray(traits.bassPattern)) {
        return generateCustomBassPattern;
    }

    switch (name) {
        case 'dynamic': return generateDynamicBass;
        case 'root_fifth': return generateRootFifthBass;
        case 'octaves': return generateOctaveBass;
        case 'walking_chromatic': return generateWalkingChromatic;
        case 'pump': return generateOctavePump;
        case 'glide': return generate808Glide;
        case 'slap': return generateFunkSlap;
        case 'gallop': return generateGallop;
        case 'stepwise': return generateStepwiseBass;
        case 'line_cliche': return generateLineCliche;
        case 'baroque_walking': return generateBaroqueWalkingBass;
        case 'drone': return generateDroneBass;
        case 'medieval_drone': return generateDroneBass; // Alias
        case 'pedal': return generateDroneBass; // Alias
        case 'running_eighths': return generateRunningEighths;
        case 'cello_ostinato': return generateRunningEighths; // Alias
        case 'waltz_pulse': return generateWaltzPulse;
        case 'synth_pulse': return generateSynthPulse;
        case 'noir_walking': return generateNoirWalking;
        case 'holiday_pump': return generateHolidayPump;
        case 'train_beat_bass': return generateTrainBeat;
        case 'motif_locked': return generateMotifLockedBass;
        case 'motif_riff': return generateMotifRiffBass;
        case 'lofi_bass': return generateLofiBass;
        default: return null;
    }
};
